import React, { useEffect, useState } from 'react';
import {
    FavouriteChangeDetail,
    FavouriteGroupSummary,
    dispatchFavouriteChange,
    fetchFavouritesData,
    getFavouritesConfig,
    postFavouriteAction,
    toBool,
} from '../utils/favourites';

interface FavouriteGroupSelectorModalProps {
    isOpen: boolean;
    onClose: () => void;
    productId: number;
    isFavourite: boolean;
    itemName?: string;
    onSuccess?: (detail: FavouriteChangeDetail) => void;
}

interface GroupSummaryResponse {
    mode?: string;
    groups?: FavouriteGroupSummary[];
    current_group?: FavouriteGroupSummary | null;
    is_favourite?: boolean;
}

interface FavouriteActionResponse {
    product_id?: number;
    message?: string;
    is_favourite?: boolean | number | string;
    button_text?: string;
    icon_class?: string;
    group_name?: string;
    group_slug?: string;
    group_summaries?: FavouriteGroupSummary[];
}

const buildFavouriteDetail = (
    productId: number,
    payload: FavouriteActionResponse
): FavouriteChangeDetail => ({
    productId: Number(payload.product_id || productId),
    isFavourite: toBool(payload.is_favourite),
    buttonText: String(payload.button_text || ''),
    iconClass: String(payload.icon_class || ''),
    message: String(payload.message || ''),
    groupName: typeof payload.group_name === 'string' ? payload.group_name : '',
    groupSlug: typeof payload.group_slug === 'string' ? payload.group_slug : '',
});

const FavouriteGroupSelectorModal: React.FC<FavouriteGroupSelectorModalProps> = ({
    isOpen,
    onClose,
    productId,
    isFavourite,
    itemName,
    onSuccess,
}) => {
    const runtimeConfig = getFavouritesConfig();
    const defaultGroupName = String(runtimeConfig.default_group_name || 'Default');
    const [groups, setGroups] = useState<FavouriteGroupSummary[]>([]);
    const [currentGroup, setCurrentGroup] = useState<FavouriteGroupSummary | null>(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [newGroupName, setNewGroupName] = useState('');
    const [resolvedIsFavourite, setResolvedIsFavourite] = useState(isFavourite);

    useEffect(() => {
        if (!isOpen) {
            setErrorMessage('');
            setNewGroupName('');
            setSubmitting(false);
            return;
        }

        let cancelled = false;
        setLoading(true);
        setErrorMessage('');
        setResolvedIsFavourite(isFavourite);

        fetchFavouritesData<GroupSummaryResponse>({
            view: 'groups_summary',
            product_id: productId,
        })
            .then((response) => {
                if (cancelled) {
                    return;
                }

                setGroups(Array.isArray(response.groups) ? response.groups : []);
                setCurrentGroup(response.current_group || null);
                setResolvedIsFavourite(toBool(response.is_favourite));
            })
            .catch((error) => {
                if (!cancelled) {
                    setErrorMessage(error instanceof Error ? error.message : 'Failed to load favourite groups.');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [isOpen, isFavourite, productId]);

    if (!isOpen) {
        return null;
    }

    const title = resolvedIsFavourite ? 'Manage Favourite' : 'Add to Favourites';
    const subtitle = itemName && itemName.trim() !== ''
        ? `Choose where to save ${itemName}.`
        : 'Choose an existing group or create a new one.';
    const closeButtonClassName = `selector-modal__close${submitting ? ' is-disabled' : ''}`;
    const groupActionButtonClassName = (isDisabled: boolean): string => [
        'button',
        'weka-secondary-btn',
        'selector-modal__button',
        'selector-modal__button--secondary',
        isDisabled ? 'is-disabled' : '',
    ].filter(Boolean).join(' ');
    const createGroupButtonClassName = [
        'button',
        'weka-primary-btn',
        'selector-modal__button',
        'selector-modal__button--primary',
        submitting || newGroupName.trim() === '' ? 'is-disabled' : '',
    ].filter(Boolean).join(' ');
    const removeFavouriteButtonClassName = [
        'button',
        'button-danger',
        'selector-modal__button',
        'selector-modal__button--danger',
        submitting ? 'is-disabled' : '',
    ].filter(Boolean).join(' ');

    const completeAction = (payload: FavouriteActionResponse): void => {
        if (Array.isArray(payload.group_summaries)) {
            setGroups(payload.group_summaries);
            const matchedGroup = payload.group_summaries.find((group) => group.slug === payload.group_slug) || null;
            setCurrentGroup(matchedGroup);
        }

        const detail = buildFavouriteDetail(productId, payload);
        setResolvedIsFavourite(toBool(detail.isFavourite));
        dispatchFavouriteChange(detail);
        onSuccess?.(detail);
        onClose();
    };

    const handleAssignToGroup = async (groupName: string) => {
        if (submitting) {
            return;
        }

        setSubmitting(true);
        setErrorMessage('');

        try {
            const payload = resolvedIsFavourite
                ? await postFavouriteAction<FavouriteActionResponse>('assign_favourite_group', {
                    product_id: productId,
                    group_name: groupName,
                })
                : await postFavouriteAction<FavouriteActionResponse>('toggle_favourite', {
                    product_id: productId,
                    group_name: groupName,
                });

            completeAction(payload);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to update favourite group.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCreateAndAssign = async () => {
        const normalizedGroupName = newGroupName.trim();
        if (normalizedGroupName === '' || submitting) {
            return;
        }

        setSubmitting(true);
        setErrorMessage('');

        try {
            const createResponse = await postFavouriteAction<{ group_summaries?: FavouriteGroupSummary[] }>('create_favourite_group', {
                group_name: normalizedGroupName,
            });

            if (Array.isArray(createResponse.group_summaries)) {
                setGroups(createResponse.group_summaries);
            }

            const payload = resolvedIsFavourite
                ? await postFavouriteAction<FavouriteActionResponse>('assign_favourite_group', {
                    product_id: productId,
                    group_name: normalizedGroupName,
                })
                : await postFavouriteAction<FavouriteActionResponse>('toggle_favourite', {
                    product_id: productId,
                    group_name: normalizedGroupName,
                });

            setNewGroupName('');
            completeAction(payload);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to create favourite group.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemoveFavourite = async () => {
        if (!resolvedIsFavourite || submitting) {
            return;
        }

        setSubmitting(true);
        setErrorMessage('');

        try {
            const payload = await postFavouriteAction<FavouriteActionResponse>('toggle_favourite', {
                product_id: productId,
            });
            completeAction(payload);
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to remove favourite.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="selector-modal-overlay" onClick={() => { if (!submitting) onClose(); }}>
            <div
                className="selector-modal"
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="favourite-group-selector-title"
            >
                <div className="selector-modal__header">
                    <div>
                        <div id="favourite-group-selector-title" className="selector-modal__title">{title}</div>
                        <p className="selector-modal__subtitle">{subtitle}</p>
                    </div>
                    <button
                        type="button"
                        aria-label="Close"
                        disabled={submitting}
                        onClick={onClose}
                        className={closeButtonClassName}
                    >
                        &times;
                    </button>
                </div>

                <div className="selector-modal__body">
                    {errorMessage !== '' && (
                        <div className="selector-modal__error" role="alert">
                            {errorMessage}
                        </div>
                    )}

                    <section className="selector-modal__section">
                        <div className="selector-modal__section-header">
                            <div className="selector-modal__section-title">Favourite Groups</div>
                            {loading && <span className="selector-modal__loading-text">Loading...</span>}
                        </div>

                        {!loading && groups.length === 0 && (
                            <div className="selector-modal__empty-state">
                                No groups available yet. {defaultGroupName} will be used automatically.
                            </div>
                        )}

                        {currentGroup && resolvedIsFavourite && (
                            <p className="selector-modal__section-hint">
                                Current group: <strong>{currentGroup.name}</strong>
                            </p>
                        )}

                        <div className="selector-modal__list">
                            {groups.map((group) => {
                                const isCurrentGroup = currentGroup?.slug === group.slug;
                                const actionLabel = resolvedIsFavourite
                                    ? (isCurrentGroup ? 'Current Group' : 'Move Here')
                                    : 'Add Here';

                                return (
                                    <div key={group.slug} className="selector-modal__list-item">
                                        <div className="selector-modal__list-meta">
                                            <p className="selector-modal__list-title">{group.name}</p>
                                            <p className="selector-modal__list-summary">
                                                {group.count} {group.count === 1 ? 'item' : 'items'}
                                                {group.is_default ? ' | Default group' : ''}
                                            </p>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => { void handleAssignToGroup(group.name); }}
                                            disabled={submitting || (resolvedIsFavourite && isCurrentGroup)}
                                            className={groupActionButtonClassName(submitting || (resolvedIsFavourite && isCurrentGroup))}
                                        >
                                            {submitting ? 'Saving...' : actionLabel}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>
                    </section>

                    <section className="selector-modal__section selector-modal__section--divided">
                        <div>
                            <div className="selector-modal__section-title">Create New Group</div>
                            <p className="selector-modal__section-hint">Create a group and save this favourite into it immediately.</p>
                        </div>
                        <div className="selector-modal__input-row">
                            <input
                                type="text"
                                placeholder="Group name"
                                value={newGroupName}
                                onChange={(event) => setNewGroupName(event.currentTarget.value)}
                                disabled={submitting}
                                className="selector-modal__input"
                            />
                            <button
                                type="button"
                                onClick={() => { void handleCreateAndAssign(); }}
                                disabled={submitting || newGroupName.trim() === ''}
                                className={createGroupButtonClassName}
                            >
                                {submitting ? 'Saving...' : (resolvedIsFavourite ? 'Create and Move' : 'Create and Add')}
                            </button>
                        </div>
                    </section>

                    {resolvedIsFavourite && (
                        <section className="selector-modal__section selector-modal__section--divided">
                            <div>
                                <div className="selector-modal__section-title">Remove Favourite</div>
                                <p className="selector-modal__section-hint">This removes the product from all favourite groups.</p>
                            </div>
                            <button
                                type="button"
                                onClick={() => { void handleRemoveFavourite(); }}
                                disabled={submitting}
                                className={removeFavouriteButtonClassName}
                            >
                                {submitting ? 'Removing...' : 'Remove from Favourites'}
                            </button>
                        </section>
                    )}
                </div>
            </div>
        </div>
    );
};

export default FavouriteGroupSelectorModal;
